import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { Minus, Plus, MessageCircle, ShoppingBag, ShieldCheck, Truck } from "lucide-react";
import { Button } from "@/components/ui/button";
import ProductPlaceholder from "@/components/site/ProductPlaceholder";
import { useStore } from "@/lib/store";
import { translations, formatPrice, BRAND } from "@/lib/i18n";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Product() {
  const { slug } = useParams();
  const { lang, addToCart } = useStore();
  const t = translations[lang];
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [color, setColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    axios.get(`${API}/products/${slug}`).then((r) => {
      setProduct(r.data);
      setSize(r.data.sizes?.[0] || "");
      setColor(r.data.colors?.[0] || "");
      setActiveImg(0);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug]);

  if (!product) {
    return <div className="max-w-7xl mx-auto px-6 py-20 text-white/50">···</div>;
  }
  const name = lang === "fr" ? product.name_fr : product.name_en;
  const desc = lang === "fr" ? product.description_fr : product.description_en;

  // Build the gallery: always show up to 3 thumbnails.
  // If the product has fewer than 3 images, the missing slots show a placeholder.
  const images = Array.isArray(product.images) ? product.images : [];
  const gallerySlots = [0, 1, 2].map((i) => images[i] || null);
  const mainSrc = gallerySlots[activeImg] || images[0] || null;

  // Apply promo (-5%) if badge says so. Used for cart price, WhatsApp message and backend order.
  const isPromo = product.badge === "PROMO -5%";
  const effectivePrice = isPromo ? Math.round(product.price_gnf * 0.95) : product.price_gnf;

  const handleAdd = () =>
    addToCart({ ...product, price_gnf: effectivePrice }, { size, color, quantity });

  const handleWhatsApp = () => {
    const opts = [size, color].filter(Boolean).join(" / ");
    const total = formatPrice(effectivePrice * quantity, lang);
    // Fire-and-forget order intent
    axios
      .post(`${API}/orders`, {
        items: [{
          slug: product.slug,
          name_fr: product.name_fr,
          name_en: product.name_en,
          price_gnf: effectivePrice,
          quantity,
          size: size || null,
          color: color || null,
          image: product.images?.[0] || null,
        }],
        total_gnf: effectivePrice * quantity,
        lang,
        source: "whatsapp_product",
      })
      .catch(() => {});
    const intro =
      lang === "fr"
        ? "🛒 NOUVELLE COMMANDE BRAF_ZAK 🛒"
        : "🛒 NEW BRAF_ZAK ORDER 🛒";
    const promoNote = isPromo ? " (PROMO -5%)" : "";
    const siteOrigin = typeof window !== "undefined" ? window.location.origin : "https://brafzak.shop";
    const productUrl = `${siteOrigin}/product/${product.slug}`;
    let imgUrl = product.images?.[0] || "";
    if (imgUrl) {
      if (imgUrl.startsWith("/api/")) imgUrl = `${process.env.REACT_APP_BACKEND_URL}${imgUrl}`;
      else if (imgUrl.startsWith("/")) imgUrl = `${siteOrigin}${imgUrl}`;
    }
    const imgLine = imgUrl ? `📷 ${imgUrl}\n` : "";
    const optsLine = opts ? `🎨 ${opts}\n` : "";
    const text = `${intro}\n\n━━━━━━━━━━━━\n📦 ${name}${promoNote}\n${optsLine}🔢 Quantité : ${quantity}\n💰 Total : ${total}\n🔗 ${productUrl}\n${imgLine}━━━━━━━━━━━━\n\n✅ Merci, l'équipe Braf_Zak vous répond vite ! ⚽👑`;
    const url = `https://wa.me/${BRAND.whatsapp}?text=${encodeURIComponent(text)}`;
    window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-10" data-testid="product-page">
      <Link to="/shop" className="text-sm text-yellow-300/80 hover:text-yellow-300" data-testid="product-back-link">
        {t.product_back}
      </Link>
      <div className="grid md:grid-cols-2 gap-10 mt-6">
        <div data-testid="product-gallery">
          <div
            className="aspect-square rounded-3xl overflow-hidden border border-yellow-500/15"
            style={{ boxShadow: "0 30px 60px -20px rgba(212,175,55,0.2)" }}
            data-testid="product-image"
          >
            {mainSrc ? (
              <img
                key={mainSrc}
                src={mainSrc}
                alt={name}
                decoding="async"
                fetchpriority="high"
                className="w-full h-full object-cover transition-opacity duration-300"
              />
            ) : (
              <ProductPlaceholder product={product} size="large" />
            )}
          </div>
          <div className="grid grid-cols-3 gap-3 mt-4">
            {gallerySlots.map((src, i) => {
              const isActive = activeImg === i;
              return (
                <button
                  key={src || `slot-${i}`}
                  type="button"
                  onClick={() => setActiveImg(i)}
                  data-testid={`product-thumb-${i}`}
                  className={`relative aspect-square rounded-xl overflow-hidden border transition-all ${
                    isActive
                      ? "border-yellow-400 ring-2 ring-yellow-400/50"
                      : "border-yellow-500/20 hover:border-yellow-500/50"
                  }`}
                  style={{ background: "rgba(0,0,0,0.4)" }}
                >
                  {src ? (
                    <img src={src} alt={`${name} ${i + 1}`} loading="lazy" decoding="async" className="w-full h-full object-cover" />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center">
                      <ProductPlaceholder product={product} size="small" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>
        <div>
          {product.badge && (
            <span
              className="inline-block text-[10px] font-black tracking-[0.25em] px-3 py-1 rounded-full mb-4"
              style={{
                background: "linear-gradient(180deg, #F4D77A, #D4AF37)",
                color: "#1a0f00",
              }}
            >
              ✦ {product.badge}
            </span>
          )}
          <h1
            className="text-4xl sm:text-5xl font-black text-white"
            style={{ fontFamily: '"Bebas Neue", system-ui', letterSpacing: "0.02em" }}
            data-testid="product-name"
          >
            {name}
          </h1>
          <p
            className="text-3xl mt-3 font-black flex items-baseline gap-3"
            style={{ fontFamily: '"Bebas Neue", system-ui' }}
            data-testid="product-price"
          >
            {product.badge === "PROMO -5%" ? (
              <>
                <span className="text-white/40 text-xl line-through font-normal">
                  {formatPrice(product.price_gnf, lang)}
                </span>
                <span style={{ color: "#F4D77A" }}>
                  {formatPrice(Math.round(product.price_gnf * 0.95), lang)}
                </span>
                <span
                  data-testid="product-promo-badge"
                  className="text-[11px] font-bold uppercase px-2.5 py-1 rounded-full text-black animate-pulse"
                  style={{ background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }}
                >
                  -5% promo
                </span>
              </>
            ) : (
              <span style={{ color: "#F4D77A" }}>{formatPrice(product.price_gnf, lang)}</span>
            )}
          </p>
          {(product.stock ?? 0) > 0 ? (
            <p
              className="text-xs mt-2 tracking-widest uppercase flex items-center gap-2"
              data-testid="product-stock-status"
            >
              <span className="text-green-400">● {t.product_in_stock}</span>
              <span className="text-yellow-300/90 normal-case tracking-normal">
                · {product.stock} {lang === "fr" ? "disponible" : "available"}{(product.stock > 1 && lang === "fr") ? "s" : ""}
              </span>
            </p>
          ) : (
            <p className="text-xs text-red-400 mt-2 tracking-widest uppercase" data-testid="product-stock-status">
              ○ {t.product_out_of_stock}
            </p>
          )}

          {product.sizes?.length > 0 && (
            <div className="mt-6">
              <p className="text-xs uppercase tracking-[0.3em] text-yellow-300/80 mb-3">
                {t.product_size}
              </p>
              <div className="flex flex-wrap gap-2" data-testid="size-selector">
                {product.sizes.map((s) => (
                  <button
                    key={s}
                    onClick={() => setSize(s)}
                    data-testid={`size-${s}`}
                    className={`min-w-[3rem] px-4 py-2 rounded-md text-sm font-bold transition-all ${
                      size === s
                        ? "text-black"
                        : "text-white/80 border border-yellow-500/30 hover:border-yellow-400"
                    }`}
                    style={
                      size === s
                        ? { background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }
                        : undefined
                    }
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          )}

          {product.colors?.length > 0 ? (
            <div className="mt-5">
              <p className="text-xs uppercase tracking-[0.3em] text-yellow-300/80 mb-3">
                {t.product_color}
              </p>
              <div className="flex flex-wrap gap-2" data-testid="color-selector">
                {product.colors.map((c) => (
                  <button
                    key={c}
                    onClick={() => setColor(c)}
                    className={`px-4 py-2 rounded-md text-sm font-bold transition-all ${
                      color === c
                        ? "text-black"
                        : "text-white/80 border border-yellow-500/30 hover:border-yellow-400"
                    }`}
                    style={
                      color === c
                        ? { background: "linear-gradient(180deg, #F4D77A, #D4AF37)" }
                        : undefined
                    }
                  >
                    {c}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <div className="mt-5">
              <p className="text-xs uppercase tracking-[0.3em] text-yellow-300/80 mb-2">
                {t.product_color}
              </p>
              <span
                data-testid="single-color-badge"
                className="inline-flex items-center gap-2 text-xs font-bold px-3 py-1.5 rounded-full border border-yellow-500/40 text-yellow-300/90"
              >
                ✦ {lang === "fr" ? "Couleur unique selon l'image" : "Single color as shown"}
              </span>
            </div>
          )}          <div className="mt-6">
            <p className="text-xs uppercase tracking-[0.3em] text-yellow-300/80 mb-3">
              {t.product_quantity}
            </p>
            <div className="inline-flex items-center border border-yellow-500/30 rounded-full">
              <button
                onClick={() => setQuantity((q) => Math.max(1, q - 1))}
                data-testid="qty-dec"
                className="w-10 h-10 flex items-center justify-center text-yellow-300 hover:bg-yellow-500/10 rounded-full"
              >
                <Minus className="w-4 h-4" />
              </button>
              <span className="w-12 text-center font-bold" data-testid="qty-value">{quantity}</span>
              <button
                onClick={() => setQuantity((q) => q + 1)}
                data-testid="qty-inc"
                className="w-10 h-10 flex items-center justify-center text-yellow-300 hover:bg-yellow-500/10 rounded-full"
              >
                <Plus className="w-4 h-4" />
              </button>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-3 mt-8">
            <Button
              onClick={handleAdd}
              disabled={(product.stock ?? 0) <= 0}
              data-testid="add-to-cart-btn"
              className="flex-1 rounded-full font-bold tracking-widest text-black py-6 hover:scale-[1.01] transition-transform disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100"
              style={{
                background: "linear-gradient(180deg, #F4D77A, #D4AF37)",
                boxShadow: "0 12px 30px -8px rgba(212,175,55,0.5)",
              }}
            >
              <ShoppingBag className="w-4 h-4 mr-2" />
              {t.product_add_to_cart}
            </Button>
            <Button
              onClick={handleWhatsApp}
              disabled={(product.stock ?? 0) <= 0}
              data-testid="order-whatsapp-btn"
              className="flex-1 rounded-full font-bold tracking-widest py-6 text-white disabled:opacity-40 disabled:cursor-not-allowed"
              style={{
                background: "linear-gradient(180deg, #25D366 0%, #128C7E 100%)",
                boxShadow: "0 12px 30px -8px rgba(37,211,102,0.4)",
              }}
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              {t.product_order_whatsapp}
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3 mt-8 text-[11px] text-white/55 uppercase tracking-wider">
            <div className="flex flex-col items-start gap-1 p-3 rounded-lg border border-yellow-500/15">
              <Truck className="w-4 h-4 text-yellow-400" />
              {t.free_shipping}
            </div>
            <div className="flex flex-col items-start gap-1 p-3 rounded-lg border border-yellow-500/15">
              <ShieldCheck className="w-4 h-4 text-yellow-400" />
              {t.secure_order}
            </div>
            <div className="flex flex-col items-start gap-1 p-3 rounded-lg border border-yellow-500/15">
              <MessageCircle className="w-4 h-4 text-yellow-400" />
              {t.pay_on_delivery}
            </div>
          </div>

          <div className="mt-10">
            <h3 className="text-xs uppercase tracking-[0.3em] text-yellow-300/80 mb-3">
              {t.product_description}
            </h3>
            <p className="text-white/70 leading-relaxed">{desc}</p>
          </div>
        </div>
      </div>
    </div>
  );
}
